import {
  BodyMedium,
  BodySmall,
  Breadcrumb,
  ConditionalWrapper,
  HeadingLarge,
  HeadingSmallWDescription,
  Tooltip,
  theme,
  usePath,
} from '@arnold/common';
import { ProductCode } from '@arnold/core';
import React, { useEffect, useState } from 'react';
import { Button, Form, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Loading } from '..';
import {
  ActiveRespondentsQuery,
  AnonymityLevel,
  UserQuery,
  useActiveRespondentsQuery,
  useOrganizationAdminsLazyQuery,
} from '../../generated/hooks';
import { sortRespondents } from '../../lib/common';
import { getDefaultRecipientOrgAdminEmail, isPulserTopicGroup, isValidEmail } from '../../lib/helpers';
import { Card, FormGroupLabel, FormRadioInput, PageWrapper, TitleCard } from '../Common';
import NewRespondentsBox from './NewRespondentsBox';
import SurveyParticipantsInput from './SurveyParticipantsInput';
import SurveySchedulingInput from './SurveySchedulingInput';
import { AllowedTopicGroup, FormResults } from './formTypes';
import ee from '../../lib/eventEmitter';
import { SurveyFormCreditCost } from './SurveyFormCreditCost';

export type SurveyDates = {
  sendAt: any;
  endsAt?: any | null;
  reminders?: any[];
};

type FormProps = {
  user: UserQuery['user'];
  surveyId?: number;
  subjectLabel: string;
  isEdit: boolean;
  loading: boolean;
  surveyDates: SurveyDates;
  respondents?: NonNullable<ActiveRespondentsQuery['user']['organization']>['activeRespondents'];
  onSave: (data: FormResults) => void;
  hasMissingTranslations: (
    respondents: NonNullable<ActiveRespondentsQuery['user']['organization']>['activeRespondents'],
  ) => boolean;
  setNewRespondents?: (respondents: string[]) => void;
  sendEmailSms?: boolean;
  onPublicSurveySave?: (endsAt?: Date) => void;
  topicGroup?: AllowedTopicGroup;
};

export enum SurveyType {
  PRIVATE,
  PUBLIC,
}

const SurveyForm = ({
  user,
  surveyId,
  subjectLabel,
  surveyDates,
  isEdit,
  respondents = [],
  loading,
  onSave,
  hasMissingTranslations,
  setNewRespondents,
  sendEmailSms = false,
  onPublicSurveySave,
  topicGroup,
}: FormProps) => {
  const { organization } = user;
  const { t } = useTranslation('surveyForm');
  const { surveysPath } = usePath();
  const anonymityLocked = isPulserTopicGroup(topicGroup);
  const [formValues, setFormValues] = useState<FormResults>(() => ({
    sendAt: new Date(surveyDates.sendAt),
    endsAt: new Date(surveyDates.endsAt),
    reminders: surveyDates.reminders?.filter(Boolean).map((r) => new Date(r)) || [],
    whitelist: !!respondents.length,
    respondents: sortRespondents([...respondents]),
    sendEmailSms,
    anonymityLevels: anonymityLocked
      ? [AnonymityLevel.Organization]
      : [AnonymityLevel.None, AnonymityLevel.Organization],
    type: SurveyType.PRIVATE,
  }));
  const [defaultOrgAdminRecipient, setDefaultOrgAdminRecipient] = useState<string | undefined | false>();
  const { loading: respondentsLoading, data: respondentsData } = useActiveRespondentsQuery({ fetchPolicy: 'no-cache' });
  const activeRespondents = respondentsData?.user.organization?.activeRespondents || [];

  const allEmailsValid = () =>
    !formValues.participants?.length ||
    (formValues.participants.every((email) => isValidEmail(email.trim())) &&
      new Set(formValues.participants.map((p) => p.trim())).size === formValues.participants.length);

  const [getOrgAdmins] = useOrganizationAdminsLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (responseData) => {
      const defaultRecipient = getDefaultRecipientOrgAdminEmail(responseData.organizationAdmins || [], user);
      setDefaultOrgAdminRecipient(defaultRecipient);

      if (!defaultRecipient) {
        return;
      }
      setFormValues({
        ...formValues,
        participants: [defaultRecipient],
      });
    },
  });

  useEffect(() => {
    if (organization && showNewRespondentsForm) {
      getOrgAdmins({
        variables: {
          organizationId: organization.id,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!organization) {
    return <></>;
  }

  const showNewRespondentsForm =
    !isEdit && organization?.enabledRespondentsCount <= 1 && formValues.type !== SurveyType.PUBLIC;

  const isSubmitDisabled =
    loading ||
    (hasMissingTranslations(formValues.respondents.length ? formValues.respondents : activeRespondents) &&
      formValues.type !== SurveyType.PUBLIC) ||
    !allEmailsValid() ||
    (formValues.whitelist && !formValues.respondents.length) ||
    !!formValues.reminders?.some(
      (r) =>
        r &&
        (r.getTime() > formValues.endsAt?.getTime() ||
          r.getTime() < formValues.sendAt.getTime() ||
          r.getTime() < new Date().getTime()),
    );

  const handleFormSubmit = (e?: React.SyntheticEvent<HTMLFormElement, Event>) => {
    if (e) {
      e.preventDefault();
    }
    if (formValues.type === SurveyType.PUBLIC && onPublicSurveySave) {
      onPublicSurveySave(formValues.endsAt);
      return;
    }

    if (showNewRespondentsForm) {
      setNewRespondents?.(
        (formValues.participants || []).filter((participant) => participant !== defaultOrgAdminRecipient),
      );
    }
    onSave(anonymityLocked ? { ...formValues, anonymityLevels: [AnonymityLevel.Organization] } : formValues);
  };

  return (
    <PageWrapper data-cy="admin-page-content">
      <Breadcrumb
        classList={'mt-n6'}
        items={[
          { title: t('AuthorizedPage:standaloneSurveys'), link: surveysPath.createPath() },
          {
            title: t('surveysOverview:chooseSurveyTopic'),
            link: '/conversationSubjects',
          },
          { title: `${t('scheduleTitle')}: ${subjectLabel}`, active: true },
        ]}
      />
      <HeadingLarge>
        {t('scheduleTitle')}: {subjectLabel}
      </HeadingLarge>

      {(loading || respondentsLoading) && <Loading />}
      {!loading && !respondentsLoading && (
        <Form onSubmit={() => ee.emitModalConfirmSurveySchedule(handleFormSubmit)}>
          {showNewRespondentsForm && (
            <div data-cy="admin-survey-new-respondents">
              <TitleCard>
                <HeadingSmallWDescription>{t('SequenceSteps:participants')}</HeadingSmallWDescription>
                <BodySmall>{t('participantsDescription')}</BodySmall>
              </TitleCard>
              <Card margin={`0 0 ${theme.spacing.j} 0`} data-icom={'form-new-respondents'}>
                <NewRespondentsBox
                  participants={formValues.participants}
                  setParticipants={(participants: string[]) => {
                    setFormValues((oldValues) => ({
                      ...oldValues,
                      participants,
                    }));
                  }}
                />
              </Card>
            </div>
          )}
          {organization.productVersion?.product &&
            [ProductCode.Business, ProductCode.Credits].includes(
              organization.productVersion.product as ProductCode,
            ) && (
              <>
                <TitleCard>
                  <HeadingSmallWDescription>{t('surveyType')}</HeadingSmallWDescription>
                  <BodySmall>{t('surveyTypeDescription')}</BodySmall>
                </TitleCard>
                <Card margin={`0 0 ${theme.spacing.j} 0`} data-icom={'form-survey-type'}>
                  <FormGroupLabel>{t('surveyType')}</FormGroupLabel>
                  <FormGroup className="form-check-group" data-icom={'form-survey-type-select'}>
                    <FormRadioInput
                      name="type"
                      dataCy="admin-survey-form-type-private"
                      clicked={() => {
                        setFormValues((oldValues) => ({
                          ...oldValues,
                          type: SurveyType.PRIVATE,
                          endsAt: new Date(surveyDates.endsAt),
                        }));
                      }}
                      defaultChecked={formValues.type === SurveyType.PRIVATE}
                      label={t('typePrivate')}
                    />
                    <FormRadioInput
                      name="type"
                      dataCy="admin-survey-form-type-public"
                      dataIcom="admin-survey-form-type-public"
                      clicked={() => {
                        setFormValues((oldValues) => ({
                          ...oldValues,
                          type: SurveyType.PUBLIC,
                          endsAt: undefined,
                          reminders: [],
                        }));
                      }}
                      defaultChecked={formValues.type === SurveyType.PUBLIC}
                      label={t('typePublic')}
                    />
                    <BodyMedium color={theme.colors.text.secondary} margin={`${theme.spacing.f} 0 0 0`}>
                      {t('typeDescription')}
                    </BodyMedium>
                  </FormGroup>
                </Card>
              </>
            )}
          <TitleCard>
            <HeadingSmallWDescription data-icom="heading-dates-and-times">
              {t('datesAndTimes')}
            </HeadingSmallWDescription>
            <BodySmall>
              {formValues.type === SurveyType.PUBLIC
                ? t('dateAndTimesDescriptionPublic')
                : t('dateAndTimesDescription')}
            </BodySmall>
          </TitleCard>
          <SurveySchedulingInput
            notStarted={true}
            formValues={formValues}
            setFormValues={setFormValues}
            showNote={formValues.type === SurveyType.PRIVATE}
            isPublic={formValues.type === SurveyType.PUBLIC}
          />
          {formValues.type === SurveyType.PRIVATE && (
            <>
              <TitleCard>
                <HeadingSmallWDescription>{t('anonymityTitle')}</HeadingSmallWDescription>
                <BodySmall>{t('anonymityDescription')}</BodySmall>
              </TitleCard>
              <Card margin={`0 0 ${theme.spacing.j} 0`} data-icom={'form-survey-anonymity'}>
                <FormGroupLabel>{t('anonymityQuestion')}</FormGroupLabel>
                <FormGroup className="form-check-group" data-icom={'form-survey-anonymity-select'}>
                  <FormRadioInput
                    name="anonymity"
                    dataCy="admin-edit-survey-anonymity-both"
                    disabled={anonymityLocked}
                    clicked={() => {
                      setFormValues((oldValues) => ({
                        ...oldValues,
                        anonymityLevels: [AnonymityLevel.None, AnonymityLevel.Organization],
                      }));
                    }}
                    defaultChecked={
                      formValues.anonymityLevels[0] === AnonymityLevel.None &&
                      formValues.anonymityLevels[1] === AnonymityLevel.Organization
                    }
                    label={t('anonymityOptionBoth')}
                  />
                  <FormRadioInput
                    name="anonymity"
                    dataCy="admin-edit-survey-anonymity-both-default-anonymous"
                    disabled={anonymityLocked}
                    clicked={() => {
                      setFormValues((oldValues) => ({
                        ...oldValues,
                        anonymityLevels: [AnonymityLevel.Organization, AnonymityLevel.None],
                      }));
                    }}
                    defaultChecked={
                      formValues.anonymityLevels[0] === AnonymityLevel.Organization &&
                      formValues.anonymityLevels[1] === AnonymityLevel.None
                    }
                    label={t('anonymityOptionBothAnonymousDefault')}
                  />
                  <FormRadioInput
                    name="anonymity"
                    dataCy="admin-edit-survey-anonymity-name"
                    disabled={anonymityLocked}
                    clicked={() => {
                      setFormValues((oldValues) => ({
                        ...oldValues,
                        anonymityLevels: [AnonymityLevel.None],
                      }));
                    }}
                    defaultChecked={
                      formValues.anonymityLevels.includes(AnonymityLevel.None) &&
                      !formValues.anonymityLevels.includes(AnonymityLevel.Organization)
                    }
                    label={t('anonymityOptionName')}
                  />
                  <FormRadioInput
                    name="anonymity"
                    dataCy="admin-edit-survey-anonymity-anonymous"
                    disabled={anonymityLocked}
                    clicked={() => {
                      setFormValues((oldValues) => ({
                        ...oldValues,
                        anonymityLevels: [AnonymityLevel.Organization],
                      }));
                    }}
                    defaultChecked={
                      !formValues.anonymityLevels.includes(AnonymityLevel.None) &&
                      formValues.anonymityLevels.includes(AnonymityLevel.Organization)
                    }
                    label={t('anonymityOptionAnonymous')}
                  />
                </FormGroup>
              </Card>

              {!showNewRespondentsForm && (
                <>
                  <TitleCard>
                    <HeadingSmallWDescription>{t('respondentsLabel')}</HeadingSmallWDescription>
                    <BodySmall>{t('respondentsDescription')}</BodySmall>
                  </TitleCard>
                  <SurveyParticipantsInput<FormResults>
                    notStarted={true}
                    setFormValues={setFormValues}
                    formValues={formValues}
                    activeRespondents={activeRespondents}
                    teams={respondentsData?.user.organization?.teams}
                    user={user}
                    surveyId={surveyId}
                  />
                </>
              )}
            </>
          )}

          <div className="d-flex flex-wrap align-items-center justify-content-between mt-7 ml-8">
            <ConditionalWrapper
              condition={
                formValues.type !== SurveyType.PUBLIC &&
                hasMissingTranslations(formValues.respondents.length ? formValues.respondents : activeRespondents)
              }
              wrapper={(children) => <Tooltip title={t('surveysOverview:cannotSchedule')}>{children}</Tooltip>}
            >
              <Button
                variant="primary"
                className={'mb-5'}
                disabled={isSubmitDisabled}
                type="submit"
                data-icom={isEdit ? undefined : 'submit-plan-survey'}
                onClick={(e) => {
                  e.preventDefault();
                  ee.emitModalConfirmSurveySchedule(handleFormSubmit);
                }}
              >
                {isEdit
                  ? t('weekPicker:save')
                  : formValues.type === SurveyType.PUBLIC
                    ? t('startSurvey')
                    : t('saveButton')}
              </Button>
            </ConditionalWrapper>
            <SurveyFormCreditCost
              organization={organization}
              formValues={formValues}
              activeRespondents={activeRespondents}
              topicGroup={topicGroup}
              t={t}
            />
          </div>
        </Form>
      )}
    </PageWrapper>
  );
};

export default SurveyForm;
